import {
  combineLatest,
  distinctUntilChanged,
  map,
  type Observable,
} from 'rxjs';
import { isEqual } from '@ardoq/common-helpers';
import {
  type AgentType,
  type APICurrentUser,
  type ArdoqId,
  type DocumentReference,
  Features,
} from '@ardoq/api-types';
import { type FeatureContext, featureOperations } from '@ardoq/features';
import { t, type I18nLocale } from '@ardoq/i18n';
import { dispatchAction } from '@ardoq/rxbeach';
import { loadedCurrentUser$ } from 'streams/currentUser/currentUser$';
import { activeAgentRunSessionIds$ } from '../../agentRunRegistry';
import { featureContext$ } from 'streams/featureContext$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { assistantMessages$ } from '../../assistantMessages$';
import type { AssistantMessagesState } from '../../assistantMessagesOperations';
import { assistantStateOperations } from '../../assistantStateOperations';
import {
  getScopedAssistantState,
  type ScopedAssistantState,
} from '../../getScopedAssistantState';
import { getAgentType } from '../../chatMessageSelectors';
import {
  getIsCancelRequestPending,
  getIsRequestPending,
  type RequestPendingStateInput,
} from '../../requestPendingState';
import { commands } from '../../commands';
import { MAX_DOCUMENTS_LIMIT } from '../../constants';
import { assistantContext$ } from '../../context/assistantContext$';
import {
  getContextTooltip,
  getDisplayName,
  getSourceIcon,
} from '../../context/contextHelpers';
import type { AssistantContext } from '../../context/types';
import { assistantDocuments$ } from '../../documents/assistantDocuments$';
import { assistantLayout$ } from '../../historyPlacement';
import type { HistoryPlacement } from '../../assistantTypes';
import {
  closeGiveContext,
  deselectDocument,
  openGiveContext,
  removeSelectedDocument,
  selectDocument,
  setDocumentSearchTerm,
  setWebSearchEnabled,
  showOrgDocumentsStep,
  uploadDocumentsFromComputer,
} from '../../documents/assistantDocumentsActions';
import {
  type AssistantDocumentsState,
  assistantDocumentsOperations,
  type GiveContextState,
  type OrgDocumentsState,
  type UploadingDocument,
} from '../../documents/assistantDocumentsOperations';
import type {
  AssistantDocumentsProps,
  GiveContextProps,
} from '../../documents/components';
import {
  toDocumentChip,
  toDocumentReference,
  toUploadingChip,
} from '../../documents/utils';
import {
  isDialogueChatMessage,
  getPendingInteractionExchangeId,
  hasActivePendingInteraction,
  getActivePlan,
  type ChatMessageId,
  type PlanStatus,
} from '../../types';
import { getHasAssistantConfigFeature } from '../../experimental/assistant-config';
import type { AssistantChatFooterProps } from './AssistantChatFooter';

/**
 * The `AssistantState` keys the footer reads. `input` is one of them, so this
 * stream legitimately re-emits per keystroke — the footer owns the controlled
 * textarea.
 */
const FOOTER_STATE_KEYS = [
  'input',
  'chatSessionId',
  'currentActiveRequest',
  'editingPlanMessageId',
  'currentAgentType',
  'cancelling',
  'showScrollDownButton',
  'hasPendingInputFocusRequest',
  'banners',
  'assistantConfig',
] as const;

type AssistantStateProps = ScopedAssistantState<typeof FOOTER_STATE_KEYS>;

type MessagesProps = {
  hasMessages: boolean;
  hasDialogueMessages: boolean;
  agentType: AgentType | undefined;
  pendingInteractionExchangeId: string | null;
  activePlan: {
    messageId: ChatMessageId;
    status: PlanStatus;
  } | null;
};

type DocumentIds = {
  orgId: ArdoqId;
  userId: ArdoqId | null;
};

type ChatFooterStreamState = {
  historyPlacement: HistoryPlacement;
  assistantState: AssistantStateProps;
  messages: MessagesProps;
  documentsState: AssistantDocumentsState;
  context: AssistantContext;
  hasWebSearchFeature: boolean;
  hasAssistantConfigFeature: boolean;
  activeAgentRunSessionIds: ReadonlySet<string>;
  documentIds: DocumentIds;
  i18nLocale: I18nLocale;
};

const toMessagesProps = (
  { messages }: AssistantMessagesState,
  currentActiveRequest: string | null
): MessagesProps => {
  const activePlan = getActivePlan(messages, currentActiveRequest);

  return {
    hasMessages: messages.length > 0,
    hasDialogueMessages: messages.some(isDialogueChatMessage),
    agentType: getAgentType(messages),
    pendingInteractionExchangeId: getPendingInteractionExchangeId(messages),
    activePlan: activePlan
      ? {
          messageId: activePlan.messageId,
          status: activePlan.content.status,
        }
      : null,
  };
};

const toDocumentIds = ({ _id, organization }: APICurrentUser): DocumentIds => ({
  orgId: organization._id,
  userId: _id,
});

const getHasWebSearchFeature = (featureContext: FeatureContext): boolean =>
  featureOperations.hasFeature(
    featureContext,
    Features.AI_CAN_ENABLE_WEB_SEARCH
  ) && featureOperations.hasFeature(featureContext, Features.AI_WEB_SEARCH);

const getPlaceholder = (
  {
    hasDialogueMessages,
    hasPendingInteraction,
    isPlanProposed,
  }: {
    hasDialogueMessages: boolean;
    hasPendingInteraction: boolean;
    isPlanProposed: boolean;
  },
  i18nLocale: I18nLocale
): string => {
  if (isPlanProposed) {
    return t(
      i18nLocale,
      'assistant:placeholder.planPending',
      'Run, edit or discard the plan above to continue.'
    );
  }

  if (hasPendingInteraction) {
    return t(
      i18nLocale,
      'assistant:placeholder.interactionPending',
      'Answer the question above to continue.'
    );
  }
  if (hasDialogueMessages) {
    return t(
      i18nLocale,
      'assistant:placeholder.followUp',
      'Ask a follow-up, or continue your analysis...'
    );
  }
  return t(i18nLocale, 'assistant:placeholder.initial', 'Ask anything...');
};

const getGiveContextProps = ({
  i18nLocale,
  giveContext,
  orgDocuments,
  selectedDocuments,
  uploadingDocuments,
  documentSearchTerm,
  webSearchEnabled,
  hasWebSearchFeature,
}: {
  i18nLocale: I18nLocale;
  giveContext: GiveContextState;
  orgDocuments: OrgDocumentsState;
  selectedDocuments: DocumentReference[];
  uploadingDocuments: UploadingDocument[];
  documentSearchTerm: string;
  webSearchEnabled: boolean;
  hasWebSearchFeature: boolean;
}): GiveContextProps => ({
  i18nLocale,
  isOpen: giveContext.isOpen,
  step: giveContext.step,
  webSearchEnabled,
  hasWebSearchFeature,
  onToggleWebSearch: enabled => dispatchAction(setWebSearchEnabled(enabled)),
  orgDocumentsView: assistantDocumentsOperations.toOrgDocumentsPickerView(
    orgDocuments,
    documentSearchTerm
  ),
  search: {
    value: documentSearchTerm,
    onValueChange: term => dispatchAction(setDocumentSearchTerm(term)),
  },
  onOpen: () => dispatchAction(openGiveContext()),
  onClose: () => dispatchAction(closeGiveContext()),
  onShowOrgDocuments: () => dispatchAction(showOrgDocumentsStep()),
  onUploadFromComputer: (files: File[]) => {
    dispatchAction(uploadDocumentsFromComputer(files));
    dispatchAction(closeGiveContext());
  },
  selectedDocumentIds: new Set(selectedDocuments.map(({ id }) => id)),
  isSelectionLimitReached:
    selectedDocuments.length + uploadingDocuments.length >= MAX_DOCUMENTS_LIMIT,
  onSelectDocument: item =>
    dispatchAction(selectDocument(toDocumentReference(item))),
  onDeselectDocument: id => dispatchAction(deselectDocument(id)),
});

const getDocumentsProps = ({
  i18nLocale,
  documentsState: {
    giveContext,
    orgDocuments,
    selectedDocuments,
    uploadingDocuments,
    documentSearchTerm,
    webSearchEnabled,
  },
  hasWebSearchFeature,
  documentIds,
}: {
  i18nLocale: I18nLocale;
  documentsState: AssistantDocumentsState;
  hasWebSearchFeature: boolean;
  documentIds: DocumentIds;
}): AssistantDocumentsProps => ({
  giveContext: getGiveContextProps({
    i18nLocale,
    giveContext,
    orgDocuments,
    selectedDocuments,
    uploadingDocuments,
    documentSearchTerm,
    webSearchEnabled,
    hasWebSearchFeature,
  }),
  selectedDocuments: [
    ...selectedDocuments.map(document => toDocumentChip(document, documentIds)),
    ...uploadingDocuments.map(toUploadingChip),
  ],
  onRemoveDocument: (document: DocumentReference) =>
    dispatchAction(removeSelectedDocument(document)),
  onDropFiles: (files: File[]) =>
    dispatchAction(uploadDocumentsFromComputer(files)),
  webSearchEnabled,
  onToggleWebSearch: enabled => dispatchAction(setWebSearchEnabled(enabled)),
});

const getInputState = ({
  activePlan,
  editingPlanMessageId,
  hasPendingInteraction,
  pendingStateInput,
  cancelling,
}: {
  activePlan: MessagesProps['activePlan'];
  editingPlanMessageId: ChatMessageId | null;
  hasPendingInteraction: boolean;
  pendingStateInput: RequestPendingStateInput;
  cancelling: boolean;
}): Pick<
  AssistantChatFooterProps,
  | 'isInputBlocked'
  | 'isEditingPlan'
  | 'isRequestPending'
  | 'isCancelRequestPending'
> & { isPlanProposed: boolean } => {
  const isEditingPlan =
    activePlan?.status === 'proposed' &&
    activePlan.messageId === editingPlanMessageId;

  const isPlanProposed = activePlan?.status === 'proposed';
  const isPlanRunning =
    activePlan?.status === 'running' || activePlan?.status === 'replanning';

  return {
    isEditingPlan,
    isPlanProposed: isPlanProposed && !isEditingPlan,
    isInputBlocked:
      hasPendingInteraction ||
      isPlanRunning ||
      (isPlanProposed && !isEditingPlan),
    isRequestPending: getIsRequestPending(pendingStateInput),
    isCancelRequestPending: getIsCancelRequestPending({
      ...pendingStateInput,
      cancelling,
    }),
  };
};

const toChatFooterProps = ({
  historyPlacement,
  assistantState: {
    input,
    chatSessionId,
    currentActiveRequest,
    editingPlanMessageId,
    cancelling,
    showScrollDownButton,
    hasPendingInputFocusRequest,
    banners,
    assistantConfig,
  },
  messages: {
    hasMessages,
    hasDialogueMessages,
    agentType,
    pendingInteractionExchangeId,
    activePlan,
  },
  documentsState,
  context,
  hasWebSearchFeature,
  hasAssistantConfigFeature,
  activeAgentRunSessionIds,
  documentIds,
  i18nLocale,
}: ChatFooterStreamState): AssistantChatFooterProps => {
  const pendingStateInput = {
    chatSessionId,
    currentActiveRequest,
    activeAgentRunSessionIds,
    activePlanStatus: activePlan?.status ?? null,
  };
  const isDedicatedAgentChat = agentType !== undefined;
  const hasPendingInteraction = hasActivePendingInteraction(
    pendingInteractionExchangeId,
    currentActiveRequest
  );

  const {
    isEditingPlan,
    isPlanProposed,
    isInputBlocked,
    isRequestPending,
    isCancelRequestPending,
  } = getInputState({
    activePlan,
    editingPlanMessageId,
    hasPendingInteraction,
    pendingStateInput,
    cancelling,
  });

  const showAssistantConfigInternal =
    hasAssistantConfigFeature && !isDedicatedAgentChat;

  return {
    commands,
    i18nLocale,
    historyPlacement,
    input,
    placeholder: getPlaceholder(
      {
        hasDialogueMessages,
        hasPendingInteraction,
        isPlanProposed,
      },
      i18nLocale
    ),
    chatSessionId,
    isRequestPending,
    isInputBlocked,
    isEditingPlan,
    isCancelRequestPending,
    canSendMessage:
      assistantStateOperations.canSendMessage({
        input,
        documentCount: documentsState.selectedDocuments.length,
      }) &&
      documentsState.uploadingDocuments.length === 0 &&
      !isInputBlocked &&
      !isEditingPlan,
    agentType,
    assistantConfigInternal: showAssistantConfigInternal
      ? assistantConfig
      : null,
    documents: getDocumentsProps({
      i18nLocale,
      documentsState,
      hasWebSearchFeature,
      documentIds,
    }),
    pageName: getDisplayName(context, i18nLocale),
    pageIcon: getSourceIcon(context),
    dataScopeTooltip: getContextTooltip(context, i18nLocale),
    hasMessages,
    showScrollDownButton,
    hasPendingInputFocusRequest,
    banners,
  };
};

const chatFooterState$: Observable<ChatFooterStreamState> = combineLatest({
  assistantState: getScopedAssistantState(FOOTER_STATE_KEYS),
  messages: combineLatest([
    assistantMessages$,
    getScopedAssistantState(['currentActiveRequest']),
  ]).pipe(
    map(([messages, { currentActiveRequest }]) =>
      toMessagesProps(messages, currentActiveRequest)
    )
  ),
  documentsState: assistantDocuments$,
  context: assistantContext$,
  hasWebSearchFeature: featureContext$.pipe(map(getHasWebSearchFeature)),
  hasAssistantConfigFeature: featureContext$.pipe(
    map(getHasAssistantConfigFeature)
  ),
  activeAgentRunSessionIds: activeAgentRunSessionIds$,
  documentIds: loadedCurrentUser$.pipe(map(toDocumentIds)),
  i18nLocale: i18nLocale$,
  historyPlacement: assistantLayout$.pipe(
    map(({ historyPlacement }) => historyPlacement),
    distinctUntilChanged()
  ),
}).pipe(distinctUntilChanged(isEqual));

export const chatFooterViewModel$: Observable<AssistantChatFooterProps> =
  chatFooterState$.pipe(map(toChatFooterProps));
